// Google Analytics 4 (GA4) event tracking + pseudonymized user identification.
// IMPORTANT: Google TOS FORBIDS sending PII (name, email, phone) to GA.
// We send only a pseudonymized user_id + non-PII properties.
// PII stays in the backend MongoDB (admin dashboard + Excel export).

const USER_ID_KEY = "aiat_ga_user_id";
const MEASUREMENT_ID = "G-ETEB72DGJ7";

// -------- User ID (pseudonymous, stable across sessions) --------

const genId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "u_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getUserId = () => {
  try {
    let id = localStorage.getItem(USER_ID_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(USER_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private mode) — return a session-only ID
    return genId();
  }
};

const isEnabled = () => typeof window !== "undefined" && typeof window.gtag === "function";

// -------- Init: configure gtag with user_id + default user properties --------

export const initAnalytics = () => {
  if (!isEnabled()) return;
  const uid = getUserId();
  try {
    window.gtag("config", MEASUREMENT_ID, {
      user_id: uid,
      send_page_view: true,
    });
    window.gtag("set", "user_properties", { app_user_id: uid });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("gtag init:", err);
  }
};

// -------- Event helpers --------

export const trackEvent = (eventName, params = {}) => {
  if (!isEnabled()) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[analytics-mock]", eventName, params);
    }
    return;
  }
  try {
    window.gtag("event", eventName, { app_user_id: getUserId(), ...params });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("gtag event:", err);
  }
};

export const trackView = (sectionName, extra = {}) =>
  trackEvent("section_view", { section: sectionName, ...extra });

export const trackClick = (label, extra = {}) =>
  trackEvent("cta_click", { label, ...extra });

export const trackSubmit = (formName, extra = {}) =>
  trackEvent("form_submit", { form: formName, ...extra });

export const trackConversion = (name, extra = {}) => trackEvent(name, extra);

/** Set non-PII user properties (e.g. preferred_style). GA remembers per user. */
export const setUserProperty = (key, value) => {
  if (!isEnabled()) return;
  try {
    window.gtag("set", "user_properties", { [key]: value });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.warn("gtag set:", err);
  }
};

// -------- Scroll depth tracking (25/50/75/90 %) --------

export const initScrollTracker = () => {
  const fired = new Set();
  const check = () => {
    const doc = document.documentElement;
    const scrolled = doc.scrollTop || document.body.scrollTop;
    const height = doc.scrollHeight - doc.clientHeight;
    if (height <= 0) return;
    const pct = (scrolled / height) * 100;
    [25, 50, 75, 90].forEach((mark) => {
      if (pct >= mark && !fired.has(mark)) {
        fired.add(mark);
        trackEvent("scroll_depth", { percent: mark });
      }
    });
  };
  window.addEventListener("scroll", check, { passive: true });
  check();
  return () => window.removeEventListener("scroll", check);
};
